/* @pareto-engineering/generator-front 1.0.12 */
import * as React from 'react';
import { useState, useRef } from 'react';
import { $createParagraphNode, $getSelection, $isRangeSelection, DEPRECATED_$isGridSelection } from 'lexical';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { INSERT_CHECK_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from '@lexical/list';
import { $createCodeNode } from '@lexical/code';
import PropTypes from 'prop-types';
import styleNames from '@pareto-engineering/bem/exports';
import "./styles.scss";
import { Popover } from "../../../../../a";
import { useOutsideClick } from "../../../../../utils";
import { blockTypeToBlockName } from "../ToolbarPlugin/ToolbarPlugin";

// Local Definitions

const baseClassName = styleNames.base;
const componentClassName = 'block-format-drop-down';
const iconsMap = {
  bullet: {
    icon: '.',
    className: 'icon'
  },
  check: {
    icon: '4',
    className: 'ai-icon'
  },
  code: {
    icon: '6',
    className: 'ai-icon'
  },
  h1: {
    icon: '1',
    className: 'ai-icon'
  },
  h2: {
    icon: '2',
    className: 'ai-icon'
  },
  h3: {
    icon: '3',
    className: 'ai-icon'
  },
  number: {
    icon: '-',
    className: 'icon'
  },
  paragraph: {
    icon: 'N',
    className: 'ai-icon'
  },
  quote: {
    icon: '5',
    className: 'ai-icon'
  }
};
/**
 * This is the component description.
 */
const BlockFormatDropDown = ({
  id,
  className: userClassName,
  style,
  editor,
  blockType
  // ...otherProps
}) => {
  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) || DEPRECATED_$isGridSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };
  const formatHeading = headingSize => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) || DEPRECATED_$isGridSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
        }
      });
    }
  };
  const formatBulletList = () => {
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };
  const formatCheckList = () => {
    if (blockType !== 'check') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };
  const formatNumberedList = () => {
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };
  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) || DEPRECATED_$isGridSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      });
    }
  };
  const formatCode = () => {
    if (blockType !== 'code') {
      editor.update(() => {
        let selection = $getSelection();
        if ($isRangeSelection(selection) || DEPRECATED_$isGridSelection(selection)) {
          if (selection.isCollapsed()) {
            $setBlocksType(selection, () => $createCodeNode());
          } else {
            const textContent = selection.getTextContent();
            const codeNode = $createCodeNode();
            selection.insertNodes([codeNode]);
            selection = $getSelection();
            if ($isRangeSelection(selection)) selection.insertRawText(textContent);
          }
        }
      });
    }
  };
  const parentRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  useOutsideClick(parentRef, () => setIsOpen(false));
  return /*#__PURE__*/React.createElement("button", {
    id: id,
    className: [baseClassName, componentClassName, userClassName, 'group'].filter(e => e).join(' '),
    style: style,
    ref: parentRef,
    type: "button",
    onClick: () => setIsOpen(!isOpen)
    // {...otherProps}
  }, /*#__PURE__*/React.createElement("span", {
    className: iconsMap[blockType].className
  }, iconsMap[blockType].icon), /*#__PURE__*/React.createElement("span", null, blockTypeToBlockName[blockType]), /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "V"), isOpen && /*#__PURE__*/React.createElement(Popover, {
    parentRef: parentRef,
    isOpen: isOpen,
    color: "background-far"
  }, /*#__PURE__*/React.createElement("button", {
    className: `item ${blockType === 'paragraph' ? 'active' : ''}`,
    onClick: formatParagraph,
    type: "button"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ai-icon"
  }, "N"), /*#__PURE__*/React.createElement("span", {
    className: "text"
  }, "Normal")), /*#__PURE__*/React.createElement("button", {
    className: `item ${blockType === 'h1' ? 'active' : ''}`,
    onClick: () => formatHeading('h1'),
    type: "button"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ai-icon"
  }, "1"), /*#__PURE__*/React.createElement("span", {
    className: "text"
  }, "Heading 1")), /*#__PURE__*/React.createElement("button", {
    className: `item ${blockType === 'h2' ? 'active' : ''}`,
    onClick: () => formatHeading('h2'),
    type: "button"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ai-icon"
  }, "2"), /*#__PURE__*/React.createElement("span", {
    className: "text"
  }, "Heading 2")), /*#__PURE__*/React.createElement("button", {
    className: `item ${blockType === 'h3' ? 'active' : ''}`,
    onClick: () => formatHeading('h3'),
    type: "button"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ai-icon"
  }, "3"), /*#__PURE__*/React.createElement("span", {
    className: "text"
  }, "Heading 3")), /*#__PURE__*/React.createElement("button", {
    className: `item ${blockType === 'bullet' ? 'active' : ''}`,
    onClick: formatBulletList,
    type: "button"
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "."), /*#__PURE__*/React.createElement("span", {
    className: "text"
  }, "Bullet List")), /*#__PURE__*/React.createElement("button", {
    className: `item ${blockType === 'number' ? 'active' : ''}`,
    onClick: formatNumberedList,
    type: "button"
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "-"), /*#__PURE__*/React.createElement("span", {
    className: "text"
  }, "Numbered List")), /*#__PURE__*/React.createElement("button", {
    className: `item ${blockType === 'check' ? 'active' : ''}`,
    onClick: formatCheckList,
    type: "button"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ai-icon"
  }, "4"), /*#__PURE__*/React.createElement("span", {
    className: "text"
  }, "Check List")), /*#__PURE__*/React.createElement("button", {
    className: `item ${blockType === 'quote' ? 'active' : ''}`,
    onClick: formatQuote,
    type: "button"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ai-icon"
  }, "5"), /*#__PURE__*/React.createElement("span", {
    className: "text"
  }, "Quote")), /*#__PURE__*/React.createElement("button", {
    className: `item ${blockType === 'code' ? 'active' : ''}`,
    onClick: formatCode,
    type: "button"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ai-icon"
  }, "6"), /*#__PURE__*/React.createElement("span", {
    className: "text"
  }, "Code Block"))));
};
BlockFormatDropDown.propTypes = {
  /**
   * The HTML id for this element
   */
  id: PropTypes.string,
  /**
   * The HTML class names for this element
   */
  className: PropTypes.string,
  /**
   * The React-written, css properties for this element.
   */
  style: PropTypes.objectOf(PropTypes.string)
};
BlockFormatDropDown.defaultProps = {
  // someProp:false
};
export default BlockFormatDropDown;